import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Phone, MessageSquare, Settings, BarChart3,
  Users, CheckCircle, XCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AICallScheduling() {
  const [settings, setSettings] = useState({
    enabled: true,
    call_duration_minutes: 15,
    working_hours_start: 9,
    working_hours_end: 18,
    timezone: 'Asia/Dubai',
    max_days_ahead: 14,
    send_whatsapp_confirmations: true,
    send_calendar_invites: true
  });
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);

  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, callsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-scheduling/settings?token=${token}`),
        axios.get(`${API_URL}/api/ai-scheduling/scheduled-calls?token=${token}&limit=20`),
        axios.get(`${API_URL}/api/ai-scheduling/analytics?token=${token}&days=30`)
      ]);
      
      if (settingsRes.data.settings) {
        setSettings(settingsRes.data.settings);
      }
      setScheduledCalls(callsRes.data.calls || []);
      setAnalytics(analyticsRes.data.analytics || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load scheduling data');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/ai-scheduling/settings?token=${token}`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const testIntentDetection = async () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-scheduling/detect-intent?token=${token}`,
        { message: testMessage }
      );
      setTestResult(response.data.detection);
      toast.success('Intent detection completed');
    } catch (error) {
      console.error('Error testing intent:', error);
      toast.error('Failed to test intent detection');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: { variant: 'default', icon: Clock, color: 'text-blue-600' },
      completed: { variant: 'success', icon: CheckCircle, color: 'text-green-600' },
      cancelled: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      no_show: { variant: 'warning', icon: AlertCircle, color: 'text-yellow-600' }
    };
    const config = variants[status] || variants.scheduled;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="ai-call-scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="h-6 w-6 text-primary" />
            AI Call Scheduling
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatically schedule calls with leads via WhatsApp
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Scheduled</p>
                  <p className="text-2xl font-bold">{analytics.total_scheduled}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{analytics.completed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">{analytics.cancelled}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-2xl font-bold text-primary">{analytics.conversion_rate}%</p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Scheduled Calls
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Test Intent
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Scheduling Settings</CardTitle>
              <CardDescription>
                Configure how AI call scheduling works for your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable AI Scheduling</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow leads to schedule calls via WhatsApp
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                  data-testid="enable-scheduling-switch"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Call Duration */}
                <div className="space-y-2">
                  <Label>Call Duration (minutes)</Label>
                  <Select
                    value={String(settings.call_duration_minutes)}
                    onValueChange={(value) => setSettings(prev => ({ 
                      ...prev, 
                      call_duration_minutes: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger data-testid="call-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Max Days Ahead */}
                <div className="space-y-2">
                  <Label>Booking Window (days)</Label>
                  <Select
                    value={String(settings.max_days_ahead)}
                    onValueChange={(value) => setSettings(prev => ({ 
                      ...prev, 
                      max_days_ahead: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Working Hours Start */}
                <div className="space-y-2">
                  <Label>Working Hours Start</Label>
                  <Select
                    value={String(settings.working_hours_start)}
                    onValueChange={(value) => setSettings(prev => ({ 
                      ...prev, 
                      working_hours_start: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i + 6)}>
                          {`${i + 6}:00 ${i + 6 < 12 ? 'AM' : 'PM'}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Working Hours End */}
                <div className="space-y-2">
                  <Label>Working Hours End</Label>
                  <Select
                    value={String(settings.working_hours_end)}
                    onValueChange={(value) => setSettings(prev => ({ 
                      ...prev, 
                      working_hours_end: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i + 12)}>
                          {`${i + 12}:00 ${i + 12 < 12 ? 'AM' : 'PM'}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Timezone */}
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={settings.timezone}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, timezone: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Dubai">Asia/Dubai (GST)</SelectItem>
                      <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                      <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Notifications</h4>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>WhatsApp Confirmations</Label>
                    <p className="text-sm text-muted-foreground">
                      Send booking confirmations via WhatsApp to lead and agent
                    </p>
                  </div>
                  <Switch
                    checked={settings.send_whatsapp_confirmations}
                    onCheckedChange={(checked) => setSettings(prev => ({ 
                      ...prev, 
                      send_whatsapp_confirmations: checked 
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Calendar Invites</Label>
                    <p className="text-sm text-muted-foreground">
                      Create calendar events for both parties
                    </p>
                  </div>
                  <Switch
                    checked={settings.send_calendar_invites}
                    onCheckedChange={(checked) => setSettings(prev => ({ 
                      ...prev, 
                      send_calendar_invites: checked 
                    }))}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Calls Tab */}
        <TabsContent value="scheduled">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Calls</CardTitle>
              <CardDescription>
                Recent calls scheduled through AI WhatsApp automation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scheduledCalls.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled calls yet</p>
                  <p className="text-sm">Calls scheduled via WhatsApp will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduledCalls.map((call) => (
                    <div
                      key={call.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`scheduled-call-${call.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Phone className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {call.contact_name || 'Unknown Contact'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {call.contact_phone} • Agent: {call.agent_name || 'Unassigned'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {new Date(call.scheduled_time).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(call.scheduled_time).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        {getStatusBadge(call.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Intent Tab */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle>Test Intent Detection</CardTitle>
              <CardDescription>
                Test how the AI detects scheduling intent from messages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Test Message</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., I want to schedule a call with an agent"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    data-testid="test-message-input"
                  />
                  <Button onClick={testIntentDetection} data-testid="test-intent-btn">
                    Test
                  </Button>
                </div>
              </div>

              {/* Quick Test Examples */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Quick examples:</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Schedule a call please',
                    'Can I speak with someone?',
                    'I want to book a meeting',
                    'Call me tomorrow morning',
                    'When is the agent available?'
                  ].map((example) => (
                    <Badge
                      key={example}
                      variant="outline"
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => setTestMessage(example)}
                    >
                      {example}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className="p-4 bg-muted rounded-lg space-y-2" data-testid="test-result">
                  <div className="flex items-center gap-2">
                    {testResult.has_scheduling_intent ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      {testResult.has_scheduling_intent 
                        ? 'Scheduling Intent Detected' 
                        : 'No Scheduling Intent'}
                    </span>
                  </div>
                  
                  {testResult.has_scheduling_intent && (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Intent Type:</span>
                          <span className="ml-2 font-medium">{testResult.intent_type}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Confidence:</span>
                          <span className="ml-2 font-medium">
                            {(testResult.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Method:</span>
                          <span className="ml-2 font-medium">{testResult.method}</span>
                        </div>
                      </div>
                      
                      {testResult.extracted_preferences && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Extracted Preferences:</span>
                          <pre className="mt-1 p-2 bg-background rounded text-xs overflow-auto">
                            {JSON.stringify(testResult.extracted_preferences, null, 2)}
                          </pre>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
